/**
 * GuidedDemoTour — 7-step compact walkthrough for demo@cartieai.com.
 *
 * v34.40 (Feb 2026) — Complete rewrite. Previous version was generic FinOps
 * (AI/GPU Intel, Zombie Killer, Commitment Intel, One-Click, Forecasting,
 * AI Command Center), missed every differentiator shipped after v34.30
 * (Why explainer, Wall of Tenants, Slack bot, @cartieai/cli, Anomaly
 * Autopsy), and had a copy-paste bug on the `violet` colorMap entry
 * (was using cyan classes).
 *
 * New lineup is curated for "what makes CARTIE different in 2026":
 *   1. Welcome (orient)
 *   2. Anomaly Autopsy — LLM root-cause in 3 bullets
 *   3. One-Click Fix + GitHub Auto-PR — we ship the diff, not just recommend
 *   4. AI CFO Advisor + Board Deck — wins the CFO buyer
 *   5. Wall of Tenants — live social proof
 *   6. Slack `@cartie` + `@cartieai/cli` — meet engineers where they work
 *   7. See all 26 engines — finale + exit to explore
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ChevronRight, ChevronLeft, X, Play,
  Sparkles, AlertOctagon, Zap, Presentation, Users, Slack, Cpu,
} from 'lucide-react';

const TOUR_STEPS = [
  {
    path: '/dashboard',
    title: 'Welcome to CARTIEAI',
    description: 'The Financial Operating System for Cloud. In the next 60 seconds you\'ll see the six surfaces that make CARTIE different from Apptio, CloudHealth, and Vantage.',
    icon: Sparkles,
    highlight: '$298K managed spend · $47K saved this month — live numbers from this dashboard',
    color: 'emerald',
  },
  {
    path: '/spike-autopsy',
    title: 'Anomaly Autopsy',
    description: '"Why is AWS up 14% this week?" — CARTIE answers in three bullets with the actual root causes, not vendor-blaming dashboards. LLM-backed, cited line items.',
    icon: AlertOctagon,
    highlight: '3-bullet root cause in <2 seconds · powered by Claude Haiku',
    color: 'rose',
  },
  {
    path: '/one-click-fix',
    title: 'One-Click Fix + GitHub Auto-PR',
    description: 'Most FinOps tools "recommend" and leave you the homework. CARTIE writes the Terraform/CDK diff and opens the PR. Merge it and the savings start that hour.',
    icon: Zap,
    highlight: '$3,918/mo of pre-staged PRs · zero engineering time, full audit trail',
    color: 'amber',
  },
  {
    path: '/ai-cfo',
    title: 'AI CFO Advisor + Board Deck',
    description: 'Board-deck generator, vendor deal intel, customer P&L, multi-currency budgeting. The CFO co-pilot that costs a SaaS subscription instead of a $50K Apptio license.',
    icon: Presentation,
    highlight: 'Board-ready deck in 90 seconds · 9 incumbents need 9 separate tools for this',
    color: 'sky',
  },
  {
    path: '/dashboard',
    title: 'Wall of Tenants — live activity',
    description: 'Watch other teams save money this minute. Anonymised, opt-in, refreshes every 8 seconds. Social proof for your team and a sanity check that the platform is alive.',
    icon: Users,
    highlight: '$saved last 7 min · pulled from real engine actions across active tenants',
    color: 'violet',
  },
  {
    path: '/slack-bot',
    title: 'Slack @cartie + @cartieai/cli',
    description: '`/cartie why is aws up 14%?` in any channel. `npx @cartieai/cli why` in your terminal. MCP server inside Claude Desktop, Cursor, Continue. We meet engineers where they already work.',
    icon: Slack,
    highlight: '3-min Slack install · 1-line CLI install · 4-tool MCP server',
    color: 'cyan',
  },
  {
    path: '/honest-scorecard',
    title: '26 proprietary engines',
    description: 'Anomaly Autopsy, Zombie Killer, Commitment Coach, Token Leak Detector, Pipeline Dedup, Egress Pre-flight, and 20 more — each independently testable, each scored against 9 incumbents.',
    icon: Cpu,
    highlight: '31/32 capabilities CARTIE passes · 265 fails across 9 incumbents · 1 honest gap we disclose',
    color: 'emerald',
  },
];

// v34.99 (Feb 14 2026) — Vibrant gradient backgrounds + white icons.
// Old pattern (bg-{c}-500/15 + text-{c}-300) looked dim/washed-out — founder
// audit. The `text`/`badge`/`btn` slots keep tinted-300 colors for highlight
// pill + button hover state (those work great on the dark card bg).
const colorMap = {
  emerald: { iconBg: 'bg-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500',     iconGlow: 'shadow-emerald-500/40', bg: 'bg-emerald-500/15', text: 'text-emerald-300', ring: 'ring-emerald-500/40', badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', dot: 'bg-emerald-400', btn: 'bg-emerald-500 hover:bg-emerald-400' },
  rose:    { iconBg: 'bg-rose-500 bg-gradient-to-br from-rose-500 to-pink-500',        iconGlow: 'shadow-rose-500/40',    bg: 'bg-rose-500/15',    text: 'text-rose-300',    ring: 'ring-rose-500/40',    badge: 'bg-rose-500/20 text-rose-300 border-rose-500/40',       dot: 'bg-rose-400',    btn: 'bg-rose-500 hover:bg-rose-400' },
  amber:   { iconBg: 'bg-amber-500 bg-gradient-to-br from-amber-500 to-orange-500',     iconGlow: 'shadow-amber-500/40',   bg: 'bg-amber-500/15',   text: 'text-amber-300',   ring: 'ring-amber-500/40',   badge: 'bg-amber-500/20 text-amber-300 border-amber-500/40',     dot: 'bg-amber-400',   btn: 'bg-amber-500 hover:bg-amber-400' },
  sky:     { iconBg: 'bg-sky-500 bg-gradient-to-br from-sky-500 to-blue-500',         iconGlow: 'shadow-sky-500/40',     bg: 'bg-sky-500/15',     text: 'text-sky-300',     ring: 'ring-sky-500/40',     badge: 'bg-sky-500/20 text-sky-300 border-sky-500/40',           dot: 'bg-sky-400',     btn: 'bg-sky-500 hover:bg-sky-400' },
  violet:  { iconBg: 'bg-violet-500 bg-gradient-to-br from-violet-500 to-fuchsia-500',   iconGlow: 'shadow-violet-500/40',  bg: 'bg-violet-500/15',  text: 'text-violet-300',  ring: 'ring-violet-500/40',  badge: 'bg-violet-500/20 text-violet-300 border-violet-500/40',  dot: 'bg-violet-400',  btn: 'bg-violet-500 hover:bg-violet-400' },
  cyan:    { iconBg: 'bg-cyan-500 bg-gradient-to-br from-cyan-500 to-sky-500',         iconGlow: 'shadow-cyan-500/40',    bg: 'bg-cyan-500/15',    text: 'text-cyan-300',    ring: 'ring-cyan-500/40',    badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',         dot: 'bg-cyan-400',    btn: 'bg-cyan-500 hover:bg-cyan-400' },
};

const GuidedDemoTour = ({ isActive, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // v35.13 — auto-minimize after 12s of inactivity so the card never sits
  // permanently over dashboard CTAs. User can still re-expand or dismiss.
  React.useEffect(() => {
    if (!isActive || isMinimized) return undefined;
    const t = setTimeout(() => setIsMinimized(true), 12_000);
    return () => clearTimeout(t);
  }, [isActive, isMinimized, currentStep]);

  if (!isActive) return null;

  const step = TOUR_STEPS[currentStep];
  const colors = colorMap[step.color];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;
  const isLast = currentStep === TOUR_STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      onClose();
      return;
    }
    const nextStep = TOUR_STEPS[currentStep + 1];
    if (location.pathname !== nextStep.path) {
      navigate(nextStep.path);
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (currentStep === 0) return;
    const prevStep = TOUR_STEPS[currentStep - 1];
    if (location.pathname !== prevStep.path) {
      navigate(prevStep.path);
    }
    setCurrentStep((prev) => prev - 1);
  };

  const handleJump = (i) => {
    if (i === currentStep) return;
    if (location.pathname !== TOUR_STEPS[i].path) {
      navigate(TOUR_STEPS[i].path);
    }
    setCurrentStep(i);
  };

  // v35.13 — Minimized "pill" state when auto-collapsed (doesn't block clicks).
  if (isMinimized) {
    return (
      <div
        className="fixed bottom-6 right-6 z-[40] pointer-events-none"
        data-testid="guided-demo-tour-minimized"
      >
        <button
          type="button"
          onClick={() => setIsMinimized(false)}
          className={`pointer-events-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-slate-950 ring-1 ${colors.ring} text-slate-100 text-[11px] font-mono shadow-lg hover:bg-slate-900 transition-colors`}
          data-testid="tour-expand-btn"
        >
          <Play className="w-3 h-3" />
          <span>Demo Tour · {currentStep + 1}/{TOUR_STEPS.length}</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-24 sm:bottom-6 left-1/2 -translate-x-1/2 z-[40] w-full max-w-md px-4 pointer-events-none"
      data-testid="guided-demo-tour"
    >
      <Card className={`pointer-events-auto bg-slate-950 border border-slate-800 ring-1 ${colors.ring} shadow-2xl text-slate-100`}>
        <CardContent className="p-5">
          {/* Header row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Badge className={`${colors.badge} border font-mono text-[10px] uppercase tracking-widest`}>
                <Play className="w-3 h-3 mr-1" />
                Demo Tour
              </Badge>
              <span className="text-xs text-slate-400 font-mono">
                {currentStep + 1} / {TOUR_STEPS.length}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-7 px-2 text-slate-400 hover:text-white hover:bg-white/5"
              data-testid="tour-close-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Progress */}
          <div className="h-1 bg-slate-800 rounded-full mb-4 overflow-hidden">
            <div
              className={`h-full rounded-full ${colors.dot} transition-all duration-500`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Content */}
          <div className="flex gap-4">
            <div className={`w-12 h-12 rounded-xl ${colors.iconBg} flex items-center justify-center flex-shrink-0 shadow-lg ${colors.iconGlow}`}>
              <Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white text-lg mb-1.5 leading-tight" data-testid="tour-step-title">
                {step.title}
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed mb-3">{step.description}</p>
              <p className={`text-xs font-medium ${colors.text} ${colors.bg} ring-1 ${colors.ring} px-3 py-1.5 rounded-lg inline-block leading-relaxed`}>
                {step.highlight}
              </p>
            </div>
          </div>

          {/* Footer / nav */}
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-800">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-40"
              data-testid="tour-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <div className="flex gap-1.5">
              {TOUR_STEPS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to step ${i + 1}`}
                  onClick={() => handleJump(i)}
                  className={`rounded-full transition-all ${
                    i === currentStep
                      ? `${colors.dot} w-6 h-1.5`
                      : i < currentStep
                        ? 'bg-slate-500 w-1.5 h-1.5 hover:bg-slate-300'
                        : 'bg-slate-700 w-1.5 h-1.5 hover:bg-slate-500'
                  }`}
                  data-testid={`tour-dot-${i}`}
                />
              ))}
            </div>

            <Button
              size="sm"
              onClick={handleNext}
              className={`${colors.btn} text-slate-950 font-semibold`}
              data-testid="tour-next-btn"
            >
              {isLast ? 'Explore on your own' : 'Next'}
              {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GuidedDemoTour;
